import { type TypedDocumentNode, gql } from '@apollo/client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, HeadlineL } from '@sorare/core/src/atoms/typography';
import { FollowButton } from '@sorare/core/src/components/user/FollowButton';
import { ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { UserNetworkInfo } from 'components/network/UserNetwork/UserNetworkInfo';
import { NeonRewardConfigs } from 'components/rewards/NeonRewards/NeonRewardConfigs';
import { isFixtureClosed } from 'lib/so5';

import type {
  UserFixtureResultsHeader_anyRewardConfigInterface,
  UserFixtureResultsHeader_so5Fixture,
  UserFixtureResultsHeader_user,
} from './__generated__/index.graphql';

const StyledUserNetworkInfo = styled(UserNetworkInfo)`
  padding: var(--triple-unit) 0;
`;
const HeaderButtonsWrapper = styled(SBHorizontal)`
  position: sticky;
  top: 0;
  width: 100%;
  padding: var(--double-unit);
  z-index: 1;
  background: var(--c-black);

  @media ${desktopAndAbove} {
    position: absolute;
    top: unset; /* so it stays in-place and does not end under */
    right: 0;
    width: auto;
    background: unset;
  }
`;

type Props = {
  so5Fixture: UserFixtureResultsHeader_so5Fixture;
  user: UserFixtureResultsHeader_user;
  totalRewardConfigs: UserFixtureResultsHeader_anyRewardConfigInterface[];
  refresh?: () => void;
};
export const UserFixtureResultsHeader = ({
  so5Fixture,
  user,
  totalRewardConfigs,
  refresh,
}: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const isDesktopAndAbove = useIsDesktopAndAbove();

  return (
    <>
      <HeaderButtonsWrapper>
        {!isDesktopAndAbove && (
          <IconButton
            size="medium"
            to={generateSportContextPath(
              ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE,
              {
                params: {
                  fixture: so5Fixture.slug,
                  eventType: so5Fixture.type,
                },
              }
            )}
            replace
            icon={faChevronLeft}
            color="tertiary"
            aria-label={formatMessage(glossary.back)}
          />
        )}
        <FollowButton
          size="small"
          subscribable={{ slug: user.slug, __typename: 'User' }}
          initialSubscription={user.followed}
          callback={refresh}
        />
      </HeaderButtonsWrapper>
      <StyledUserNetworkInfo
        user={user}
        variant="large"
        UserNameWrapper={HeadlineL}
      >
        {!!totalRewardConfigs && (
          <Vertical gap={0.5}>
            <Horizontal center>
              <BrandXXS bold color="var(--c-nd-600)">
                <FormattedMessage
                  {...(isFixtureClosed(so5Fixture)
                    ? glossary.rewards
                    : fantasy.projectedRewards)}
                />
              </BrandXXS>
            </Horizontal>
            <NeonRewardConfigs
              rewardConfigs={totalRewardConfigs}
              size={24}
              iconSize={2.5}
              displayLabels={false}
            />
          </Vertical>
        )}
      </StyledUserNetworkInfo>
    </>
  );
};

UserFixtureResultsHeader.fragments = {
  so5Fixture: gql`
    fragment UserFixtureResultsHeader_so5Fixture on So5Fixture {
      slug
      type
      ...isFixtureClosed_so5Fixture
    }
    ${isFixtureClosed.fragments.so5Fixture}
  ` as TypedDocumentNode<UserFixtureResultsHeader_so5Fixture>,
  user: gql`
    fragment UserFixtureResultsHeader_user on User {
      slug
      id
      followed {
        slug
      }
      ...UserNetworkInfo_publicUserInfoInterface
    }
    ${UserNetworkInfo.fragments.user}
  ` as TypedDocumentNode<UserFixtureResultsHeader_user>,
  anyRewardConfigInterface: gql`
    fragment UserFixtureResultsHeader_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...NeonRewardConfigs_rewardConfig
    }
    ${NeonRewardConfigs.fragments.rewardConfig}
  ` as TypedDocumentNode<UserFixtureResultsHeader_anyRewardConfigInterface>,
};
